import { Injectable, inject } from "@angular/core";
import { MITPService } from "../../services/mitp.service";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { Observable, lastValueFrom, shareReplay } from "rxjs";
import { Video } from "../../entities/videos/video";
import { MatDialog } from "@angular/material/dialog";
import { VideoPlayerComponent } from "./video-player/video-player.component";
import { VideoChapter } from "../../entities/videos/video-chapter";

@Injectable({
	providedIn: "root",
})
export class VideosService {
	private mitp = inject(MITPService);
	private http = inject(HttpClient);
	private dialog = inject(MatDialog);

	private videos$ = this.http.get<Video[]>(this.mitp.Videos()).pipe(shareReplay(1));

	getVideos(): Observable<Video[]> {
		return this.videos$;
	}

	getRecentVideos(): Observable<Video[]> {
		return this.http.get<Video[]>(this.mitp.Videos() + "/recent");
	}

	getAllVideos(): Observable<Video[]> {
		return this.http.get<Video[]>(this.mitp.Videos() + "/all");
	}

	getVideoData(videoId: number): Observable<{ playbackUrl: string; thumbnailUrl: string }> {
		return this.http.get<{ playbackUrl: string; thumbnailUrl: string }>(this.mitp.Videos() + `/${videoId}`);
	}

	createVideo(video: any): Observable<HttpResponse<Video>> {
		return this.http.post<Video>(this.mitp.Videos(), video, { observe: "response" });
	}

	getUploadUrl(): Observable<{ uploadId: string; uploadUrl: string }> {
		return this.http.post<{ uploadId: string; uploadUrl: string }>(this.mitp.Videos() + "/upload", {
			corsOrigin: window.location.origin,
		});
	}

	async playVideo(video: Video | number) {
		const dialogRef = this.dialog.openDialogs.find(
			(dialog) => dialog.componentInstance instanceof VideoPlayerComponent
		);
		if (dialogRef) {
			dialogRef.close();
		}

		if (typeof video === "number") {
			const videos = await lastValueFrom(this.getVideos());
			video = videos.find((v) => v.id === video);
		}

		this.dialog.open(VideoPlayerComponent, {
			data: video,
			width: "80%",
			maxHeight: "90vh",
			panelClass: "video-player-dialog",
			disableClose: true,
			autoFocus: false,
		});
	}

	updateVideo(
		videoId: number,
		video: { label: string; description: string | null; tags: { id: number }[] }
	): Observable<Video> {
		return this.http.put<Video>(this.mitp.Videos() + `/${videoId}`, video);
	}

	deleteVideo(videoId: number): Observable<HttpResponse<null>> {
		return this.http.delete<null>(this.mitp.Videos() + `/${videoId}`, { observe: "response" });
	}

	createChapter(videoId: number, label: string, position: number): Observable<VideoChapter> {
		return this.http.post<VideoChapter>(this.mitp.Videos() + `/${videoId}/chapters`, { label, position });
	}

	deleteChapter(videoId: number, chapterId: number): Observable<HttpResponse<null>> {
		return this.http.delete<null>(this.mitp.Videos() + `/${videoId}/chapters/${chapterId}`, {
			observe: "response",
		});
	}
}
